// The forgot-password flow: request a one-time PIN by email, then reset the password using that PIN PLUS the
// account's recovery code. Both factors are required (the PIN proves mailbox control; the recovery code
// recovers the master key so encrypted data survives). All crypto is client-side — see account.ts
// (resetPasswordWithRecovery). The server never sees the password, recovery code, or master key.
import { useState, type FormEvent } from 'react';
import { requestPasswordReset, resetPasswordWithRecovery } from './account';
import { getAccountsApi } from './accountsApi';
import type { AccountsApi } from '@wiser/accounts';
import * as s from './styles';

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError
    || (err instanceof Error && /failed to fetch|load failed|network/i.test(err.message));
}

export interface ResetPasswordFormProps {
  api?: AccountsApi;
  /** Pre-fill the email (e.g. from the sign-in form). */
  initialEmail?: string;
  /** Back to sign-in. */
  onBack: () => void;
  /** Called after a successful reset (e.g. return to sign-in with the email pre-filled). */
  onReset?: (email: string) => void;
}

type Step = 'request' | 'reset' | 'done';

export function ResetPasswordForm({ api = getAccountsApi(), initialEmail = '', onBack, onReset }: ResetPasswordFormProps) {
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState(initialEmail);
  const [pin, setPin] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestPin = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await requestPasswordReset(api, email.trim());
      // Constant-shape: we always advance, whether or not the email has an account (no existence oracle).
      setStep('reset');
    } catch (err) {
      console.error('wiser-auth reset request', err);
      setError(isNetworkError(err)
        ? 'Could not connect to the account server — check that you are online.'
        : 'Could not start the reset — try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError('The two passwords don’t match.'); return; }
    setBusy(true); setError(null);
    try {
      await resetPasswordWithRecovery(api, {
        email: email.trim(), recoveryCode, pin, newPassword: password,
      });
      setStep('done');
    } catch (err) {
      console.error('wiser-auth reset', err);
      setError(isNetworkError(err)
        ? 'Could not connect to the account server — check that you are online.'
        : 'Reset failed — check your PIN and recovery code, then try again. PINs expire after 15 minutes.');
    } finally {
      setBusy(false);
    }
  };

  if (step === 'done') {
    return (
      <>
        <h2 style={s.title}>Password reset</h2>
        <p style={s.sub}>Your password has been changed.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={s.ok}>You can now sign in with your new password. Your recovery code is unchanged.</p>
          <button type="button" style={s.primaryBtn} onClick={() => onReset?.(email.trim())}>
            Back to sign in
          </button>
        </div>
      </>
    );
  }

  if (step === 'reset') {
    return (
      <>
        <h2 style={s.title}>Reset your password</h2>
        <p style={s.sub}>
          If <strong>{email.trim()}</strong> has an account, we’ve emailed a one-time PIN. Enter it with your
          recovery code and a new password.
        </p>
        <form onSubmit={doReset} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={s.lbl}>Emailed PIN
            <input inputMode="numeric" autoComplete="one-time-code" required value={pin}
              onChange={(e) => setPin(e.target.value)} placeholder="10-digit code" style={s.input} />
          </label>
          <label style={s.lbl}>Recovery code
            <input type="text" required value={recoveryCode} spellCheck={false} autoCapitalize="characters"
              onChange={(e) => setRecoveryCode(e.target.value)} placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" style={s.input} />
          </label>
          <label style={s.lbl}>New password
            <input type="password" required minLength={8} autoComplete="new-password" value={password}
              onChange={(e) => setPassword(e.target.value)} style={s.input} />
          </label>
          <label style={s.lbl}>Confirm new password
            <input type="password" required minLength={8} autoComplete="new-password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} style={s.input} />
          </label>
          {error && <p role="alert" style={s.err}>{error}</p>}
          <button type="submit" disabled={busy} style={s.primaryBtn}>{busy ? '…' : 'Reset password'}</button>
        </form>
        <button type="button" style={s.switchBtn} onClick={() => { setStep('request'); setError(null); }}>
          Didn’t get a PIN? Start over
        </button>
      </>
    );
  }

  // step === 'request'
  return (
    <>
      <h2 style={s.title}>Forgot your password?</h2>
      <p style={s.sub}>Enter your email and we’ll send a one-time PIN to reset it with your recovery code.</p>
      <form onSubmit={requestPin} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label style={s.lbl}>Email
          <input type="email" autoComplete="username" required value={email}
            onChange={(e) => setEmail(e.target.value)} style={s.input} />
        </label>
        {error && <p role="alert" style={s.err}>{error}</p>}
        <button type="submit" disabled={busy} style={s.primaryBtn}>{busy ? '…' : 'Email me a PIN'}</button>
      </form>
      <button type="button" style={s.switchBtn} onClick={onBack}>Back to sign in</button>
    </>
  );
}
